import styled from "@emotion/styled"
import { InputAdornment, Slider, TextField, Typography } from "@mui/material"
import React from "react"
import { sharedColors } from "./theme/colors"

const StyledLayout = styled.div`
  display: flex;
  padding-bottom: 4px;
  flex-direction: column;
`

const ManualInputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  margin-bottom: 24px;
`

const LabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSlider = styled(Slider)`
  margin-bottom: 24px;
  .MuiSlider-thumb {
    background-color: ${sharedColors.darkGreen};
    height: 14px;
    width: 14px;
  }
  .MuiSlider-track {
    background-color: ${sharedColors.darkGreen};
  }
  .MuiSlider-rail {
    background-color: #ece9e9;
  }
`

// Format number with commas and no decimals
const formatNumber = (value: number): string => {
  return value.toLocaleString("he-IL", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
}

interface InputProps {
  label: string
  placeholder: string
  value: number
  onChange: (val: number) => void
  min: number
  max: number
  currency?: boolean
  disabled?: boolean
  months?: boolean
}

export default function Input({
  label,
  placeholder,
  value,
  onChange,
  min,
  max,
  currency,
  disabled,
  months,
}: InputProps) {
  const [inputValue, setInputValue] = React.useState("")
  const [isFocused, setIsFocused] = React.useState(false)

  // Update input value when value prop changes (from slider or other sources)
  React.useEffect(() => {
    if (!isFocused) {
      setInputValue(formatNumber(value))
    }
  }, [value, isFocused])

  // Handle change coming from slider
  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const numericValue = Array.isArray(newValue) ? newValue[0] : newValue
    onChange(numericValue)
  }

  // Handle manual text input
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const raw = event.target.value.replace(/[^0-9]/g, "")
    setInputValue(raw ? formatNumber(parseInt(raw, 10)) : "")
  }

  // Handle when user finishes typing (on blur)
  const handleInputBlur = () => {
    setIsFocused(false)
    const raw = inputValue.replace(/[^0-9]/g, "")
    let numericValue = raw ? parseInt(raw, 10) : 0

    // Clamp to min/max only when user finishes typing
    if (numericValue < min) numericValue = min
    if (numericValue > max) numericValue = max

    onChange(numericValue)
  }

  const handleInputFocus = () => {
    setIsFocused(true)
  }

  // Build accessible label for input field
  const currencyText = currency ? "בשקלים" : ""
  const unitText = months ? "תשלומים" : ""
  const ariaLabel =
    `${label} ${currencyText} ${unitText} בין ${formatNumber(min)} ל-${formatNumber(max)} או המשך לבחור עם סרגל גלילה`.trim()

  // Build aria-valuetext for slider
  const getAriaValueText = (sliderValue: number) => {
    const formattedValue = formatNumber(sliderValue)
    if (currency) return `${formattedValue} שקלים`
    if (months) return `${formattedValue} תשלומים`
    return formattedValue
  }

  return (
    <StyledLayout>
      <ManualInputRow>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <TextField
          label={label}
          type={"text"}
          placeholder={placeholder}
          variant="outlined"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: currency && (
              <InputAdornment position="end" style={{ alignSelf: "center" }}>
                ₪
              </InputAdornment>
            ),
          }}
          inputProps={{
            "aria-label": ariaLabel,
          }}
          sx={{
            width: "164px",
          }}
        />
      </ManualInputRow>
      <StyledSlider
        aria-label={`${label} ${currencyText} ${unitText}`.trim()}
        value={value}
        onChange={handleSliderChange}
        min={min}
        max={max}
        disabled={disabled}
        valueLabelDisplay="auto"
        valueLabelFormat={formatNumber}
        getAriaValueText={getAriaValueText}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
      />
      <LabelsRow>
        <Typography variant="body2" fontWeight={600}>
          {formatNumber(min)} {currency && "₪"} {months && "תשלום"}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {formatNumber(max)} {currency && "₪"} {months && "תשלומים"}
        </Typography>
      </LabelsRow>
    </StyledLayout>
  )
}
